// FTEDN — Notification store (Zustand)
import { create } from 'zustand';
import { DEFAULT_NOTIFICATION_PREFS, type UserNotificationPreferences } from '@ftedn/shared';
import { apiClient } from '../hooks/useApi';
import { subscribeNotifications, unsubscribe } from '../services/firestore-realtime';

export type NotificationType = 'shipment' | 'program' | 'community' | 'vote' | 'achievement' | 'level' | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  read: boolean;
  xp: number;
  createdAt: string;
}

export type NotificationPreferencePatch = {
  channels?: Partial<UserNotificationPreferences['channels']>;
  quietHours?: Partial<UserNotificationPreferences['quietHours']>;
};

export const DEFAULT_CREATOR_NOTIFICATION_PREFS: UserNotificationPreferences = {
  channels: { ...DEFAULT_NOTIFICATION_PREFS.channels, inApp: true },
  quietHours: { ...DEFAULT_NOTIFICATION_PREFS.quietHours },
  mechanicOptOuts: [...DEFAULT_NOTIFICATION_PREFS.mechanicOptOuts],
};

interface NotificationState {
  items: Notification[];
  loading: boolean;
  unreadCount: number;
  realtimeActive: boolean;
  preferences: UserNotificationPreferences;
  preferencesLoading: boolean;
  preferencesSaving: boolean;
  preferencesError: string | null;
  fetchNotifications: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  updatePreferences: (patch: NotificationPreferencePatch) => Promise<boolean>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  subscribeToNotifications: (userId: string) => void;
  unsubscribeFromNotifications: () => void;
}

/** Map raw Firestore notification doc */
export function mapNotification(raw: Record<string, unknown>): Notification {
  return {
    id: String(raw.id ?? ''),
    type: (raw.type as NotificationType) ?? 'system',
    title: String(raw.title ?? ''),
    body: String(raw.body ?? raw.message ?? ''),
    read: Boolean(raw.read ?? false),
    xp: Number(raw.xp ?? 0),
    createdAt: String(raw.createdAt ?? ''),
  };
}

/** Compute unread count from items array */
export function countUnread(items: Notification[]): number {
  return items.filter(n => !n.read).length;
}

export function mapNotificationPreferences(raw: unknown): UserNotificationPreferences {
  const data = raw && typeof raw === 'object' ? raw as Partial<UserNotificationPreferences> : {};
  return {
    channels: {
      ...DEFAULT_CREATOR_NOTIFICATION_PREFS.channels,
      ...data.channels,
      inApp: true,
    },
    quietHours: {
      ...DEFAULT_CREATOR_NOTIFICATION_PREFS.quietHours,
      ...data.quietHours,
    },
    mechanicOptOuts: Array.isArray(data.mechanicOptOuts) ? data.mechanicOptOuts : [],
  };
}

function sanitizePreferencePatch(patch: NotificationPreferencePatch): NotificationPreferencePatch {
  const sanitized: NotificationPreferencePatch = {};

  if (patch.channels) {
    const channels: Partial<UserNotificationPreferences['channels']> = {};
    for (const key of ['inApp', 'sms', 'email', 'push'] as const) {
      const value = patch.channels[key];
      if (typeof value === 'boolean') channels[key] = key === 'inApp' ? true : value;
    }
    if (Object.keys(channels).length > 0) sanitized.channels = channels;
  }

  if (patch.quietHours) sanitized.quietHours = patch.quietHours;

  return sanitized;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  items: [],
  loading: false,
  unreadCount: 0,
  realtimeActive: false,
  preferences: DEFAULT_CREATOR_NOTIFICATION_PREFS,
  preferencesLoading: false,
  preferencesSaving: false,
  preferencesError: null,

  subscribeToNotifications: (userId: string) => {
    subscribeNotifications(
      userId,
      (docs) => {
        const items = docs.map(mapNotification);
        set({ items, unreadCount: countUnread(items), realtimeActive: true });
      },
      (err) => {
        console.error('[notifications] realtime error:', err.message);
        // Fall back to polling
        get().fetchNotifications();
      },
    );
  },

  unsubscribeFromNotifications: () => {
    unsubscribe('notifications');
    set({ realtimeActive: false });
  },

  fetchNotifications: async () => {
    set({ loading: true });
    try {
      const res = await apiClient.get('/notifications');
      if (res.success && Array.isArray(res.data)) {
        const items = res.data.map(mapNotification);
        set({ items, unreadCount: countUnread(items), loading: false });
      } else {
        set({ loading: false });
      }
    } catch {
      set({ loading: false });
    }
  },

  fetchPreferences: async () => {
    set({ preferencesLoading: true, preferencesError: null });
    try {
      const res = await apiClient.get('/notifications/preferences');
      if (res.success) {
        set({ preferences: mapNotificationPreferences(res.data), preferencesLoading: false });
      } else {
        set({ preferencesLoading: false });
      }
    } catch (err) {
      set({ preferencesLoading: false, preferencesError: err instanceof Error ? err.message : 'Failed to load preferences' });
    }
  },

  updatePreferences: async (patch) => {
    const previous = get().preferences;
    const sanitized = sanitizePreferencePatch(patch);
    const optimistic = mapNotificationPreferences({
      ...previous,
      channels: { ...previous.channels, ...sanitized.channels },
      quietHours: { ...previous.quietHours, ...sanitized.quietHours },
    });

    set({ preferences: optimistic, preferencesSaving: true, preferencesError: null });
    try {
      const res = await apiClient.patch('/notifications/preferences', sanitized);
      if (res.success) {
        set({ preferences: mapNotificationPreferences(res.data), preferencesSaving: false });
        return true;
      }

      set({ preferences: previous, preferencesSaving: false });
      return false;
    } catch (err) {
      set({
        preferences: previous,
        preferencesSaving: false,
        preferencesError: err instanceof Error ? err.message : 'Failed to update preferences',
      });
      return false;
    }
  },

  markRead: async (id) => {
    // Optimistic update
    const items = get().items.map(n => n.id === id ? { ...n, read: true } : n);
    set({ items, unreadCount: countUnread(items) });
    try {
      await apiClient.patch(`/notifications/${id}/read`);
    } catch { /* revert on failure if needed */ }
  },

  markAllRead: async () => {
    const items = get().items.map(n => ({ ...n, read: true }));
    set({ items, unreadCount: 0 });
    try {
      await apiClient.post('/notifications/read-all');
    } catch { /* silent */ }
  },
}));
